import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Alert,
    Card,
    CardBody,
    UncontrolledTooltip,
} from "reactstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import TableContainer from "../../../../components/Common/TableContainer";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import getAccessRights from "components/Common/getAccessRights";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomSelect from "components/Common/custom-select";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { fetchActiveEmployees, fetchEmployeeSalaryDefinition, fetchEmployeeSalaryDefinitionClear, saveEmployeeSalaryDefinition } from "store/actions";
import Swal from "sweetalert2";
import Loader from "common/pageLoader";

const SalaryDefinition = () => {

    const dispatch = useDispatch();
    const [authDetails, setAuthDetails] = useState(null);
    const [employeeSalaryDefinitionStatus, setEmployeeSalaryDefinitionStatus] = useState(null);
    const [employeeSalaryDefinitionCalculationStatus, setEmployeeSalaryDefinitionCalculationStatus] = useState(false);

    const {
        employees,
        employeeSalaryDefinition,
        loading,
        success,
        error,
    } = useSelector((state) => ({
        employees: state.EmployeeDetailsReducer?.employeesActiveList ?? [],
        employeeSalaryDefinition: state.PayrollSalaryEmployeeSalaryDefinitionReducer?.employeeSalaryDefinition ?? '',
        loading: state.PayrollSalaryEmployeeSalaryDefinitionReducer?.loading,
        error: state.PayrollSalaryEmployeeSalaryDefinitionReducer?.error,
        success: state.PayrollSalaryEmployeeSalaryDefinitionReducer?.success,
    }));

    useEffect(() => {
        resetFormData();
        const authUserDetails = useAuthUserDetails();
        setAuthDetails(authUserDetails[0])
        const unitId = authUserDetails[0].unit_id
        dispatch(fetchActiveEmployees(unitId));
    }, [dispatch]);

    useEffect(() => {
        if (success) {
            setEmployeeSalaryDefinitionData(employeeSalaryDefinition);
        }
    }, [authDetails, success, dispatch]);

    const [isEdit, setIsEdit] = useState(false);
    const [editId, setEditId] = useState(null);
    const [employeeDataSelected, setEmployeeDataSelected] = useState(null);
    const [employeeSalaryDefinitionType, setEmployeeSalaryDefinitionType] = useState('1');
    const [employeeSalaryDefinitionData, setEmployeeSalaryDefinitionData] = useState(null);
    const [employeeSalaryDefinitionTypeStatus, setEmployeeSalaryDefinitionTypeStatus] = useState(true);

    const location = useLocation();
    const history = useHistory();

    useEffect(() => {
        const accessRight = getAccessRights(location.pathname, history);
    }, [location]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            employee: "",
            employee_salary_definition_type: "1",
            gross_month: "",
            gross_year: "",
            total_gross: 0,
            employer_esi_percent: 0,
            employer_esi_amount: 0,
            employee_pf_percent: 0,
            employer_pf_amount: 0,
            total_ctc: 0,
            employee_esi_percent: 0,
            employee_esi_amount: 0,
            employee_pf_percent: 0,
            employee_pf_amount: 0,
            take_home: 0,
            allowances: [],
            header: [],
            total_gross: 0,
            validateOnChange: true,
            validateOnBlur: true,
        },
        validationSchema: Yup.object({
            // employee: Yup.object().nullable().required("Employee is required"),
            employee_salary_definition_type: Yup.string().required("Task Type is required"),
            // gross_month: Yup.number().min(0, "Negative values are not allowed").required("Gross Month is required"),
            gross_month: Yup.number().typeError("Gross Month must be a number").transform((value, originalValue) => { return originalValue === "" ? null : value; }).nullable().min(1, "Gross month is required").required("Gross Month is required").moreThan(0, "Gross Month must be greater than 0"),
            gross_year: Yup.number().typeError("Gross Year must be a number").transform((value, originalValue) => { return originalValue === "" ? null : value; }).nullable().min(0, "Gross Year is required").required("Gross Year is required").moreThan(0, "Gross Year must be greater than 0"),
            total_gross: Yup.number().required("Total Gross is required").oneOf([Yup.ref('gross_month'), null], "Gross Total and Gross Month should be equal."),
            // employee_esi_percent: Yup.number().required("Employer ESI Percentage is required"),
            // employer_esi_amount: Yup.number().required("Employer ESI Amount is required"),
            // employee_pf_percent: Yup.number().required("Employer PF Percentage is required"),
            // employer_pf_amount: Yup.number().required("Employer PF Amount is required"),
            total_ctc: Yup.number().nullable().typeError("Total CTC must be a number").required("Total CTC is required"),
            employee_pf_amount: Yup.number().required("Employee PF Amount is required"),
            take_home: Yup.number().nullable().typeError("Take home must be a number").required("Take home is required"),
            allowances: Yup.array().of(Yup.object().shape({ per_month: Yup.number().typeError("Per month must be a number").transform((value, originalValue) => originalValue === "" ? null : value).nullable().required("Per month is required").moreThan(0, "Per month must be greater than 0") })),
        }
        ),
        onSubmit: (values, { resetForm }) => {
            const formData = new FormData();
            formData.append("emp_id", employeeDataSelected.value);
            formData.append("cat_id", employeeSalaryDefinitionType);

            let employee_salary = {
                sal: formik.values.allowances?.map(item => ({
                    payallwonceid: item.payallwonceid,
                    adtype: "1",
                    percentage: Number(item.percentage) || 0,
                    per_month: Number(item.per_month) || 0,
                    salary_status: "1"
                })) || []
            };

            const extraComponents = [
                { payallwonceid: 1, adtype: 2, salary_status: 1, percentage: Number(values.employee_esi_percent) || 0, per_month: Number(values.employer_esi_amount) || 0 },
                { payallwonceid: 1, adtype: 2, salary_status: 2, percentage: Number(values.employee_esi_percent) || 0, per_month: Number(values.employee_esi_amount) || 0 },
                { payallwonceid: 2, adtype: 2, salary_status: 1, percentage: Number(values.employee_pf_percent) || 0, per_month: Number(values.employee_pf_amount) || 0 },
                { payallwonceid: 2, adtype: 2, salary_status: 2, percentage: Number(values.employee_pf_percent) || 0, per_month: Number(values.employer_pf_amount) || 0 }
            ];

            employee_salary.sal.push(...extraComponents);

            //  console.log("Employee Salary>>>>>>>>>>>>", JSON.stringify(employee_salary))
            formData.append("lst_employee_salary", JSON.stringify(employee_salary));
            formData.append("create_by", authDetails?.user_id);
            formData.append("unit_id", authDetails?.unit_id);
            dispatch(saveEmployeeSalaryDefinition(formData));
            resetFormData();
        },
    });

    const resetFormData = () => {
        formik.resetForm();
        setIsEdit(false);
        setEditId(null);
        setEmployeeDataSelected(null);
        formik.values.employee = '';
        formik.values.employee_salary_definition_type = '1';
        formik.setFieldValue("employee", '');
        setEmployeeSalaryDefinitionType('1');
        setEmployeeSalaryDefinitionTypeStatus(true);
        dispatch(fetchEmployeeSalaryDefinitionClear());
        formik.resetForm({
            values: {
                employee: "",
                employee_salary_definition_type: "1",
                gross_month: "",
                gross_year: "",
                total_gross: "",
                employer_esi_percent: "",
                employer_esi_amount: 0,
                employer_pf_percent: "",
                employer_pf_amount: 0,
                total_ctc: 0,
                employee_esi_percent: "",
                employee_esi_amount: 0,
                employee_pf_percent: "",
                employee_pf_amount: 0,
                take_home: 0
            },
        });
        setEmployeeSalaryDefinitionCalculationStatus(false);
    };

    const clearEmployeeSalaryDefinitionPercentageData = () => {
        dispatch(fetchEmployeeSalaryDefinitionClear());
    };

    const calculateGrossData = () => {
        // Manually trigger field validation
        formik.setFieldTouched('employee', true); // so error shows up
        formik.validateField('employee');

        formik.setFieldTouched('employee_salary_definition_type', true); // so error shows up
        formik.validateField('employee_salary_definition_type');

        formik.setFieldTouched('gross_month', true); // so error shows up
        formik.validateField('gross_month');

        const hasErrorEmp = formik.errors.employee && formik.touched.employee;
        const hasErrorType = formik.errors.employee_salary_definition_type && formik.touched.employee_salary_definition_type;
        const hasErrorGM = formik.errors.gross_month && formik.touched.gross_month;

        if (!hasErrorEmp && !hasErrorType && !hasErrorGM && formik.values.employee != '' && formik.values.employee.value != '' && formik.values.employee.value != undefined && formik.values.employee_salary_definition_type != '' && formik.values.gross_month >= 0) {
            setEmployeeSalaryDefinitionCalculationStatus(true);

            const formData = new FormData();
            formData.append("emp_id", formik.values.employee.value);
            formData.append("automate_type", formik.values.employee_salary_definition_type);
            formData.append("start_salary", formik.values.gross_month);
            formData.append("create_by", authDetails?.user_id);
            formData.append("unit_id", authDetails?.unit_id);
            dispatch(fetchEmployeeSalaryDefinition(formData));
        }
    };

    const findEmpSalaryDefinition = (empData) => {
        if (empData) {
            const formData = new FormData();
            formData.append("emp_id", empData.value);
            formData.append("automate_type", 0);
            formData.append("start_salary", 0);
            formData.append("create_by", authDetails?.user_id);
            formData.append("unit_id", authDetails?.unit_id);
            dispatch(fetchEmployeeSalaryDefinition(formData));
        }
    }

    useEffect(() => {
        if (employeeSalaryDefinition?.emp_salarydefshow?.length > 0) {
            const mapped = employeeSalaryDefinition.emp_salarydefshow.map(item => ({
                payallwonceid: item.payallwonceid,
                allowance: item.allowance,
                percentage: item.percentage,
                per_month: item.per_month || 0,
                per_year: item.per_year || 0
            }));

            const header = employeeSalaryDefinition.emp_personel.map(item => ({
                employer_esi_percent: item.employer_esi_percent,
                employee_esi_percent: item.employee_esi_percent,
                employer_pf_percent: item.employer_pf_percent,
                employee_pf_percent: item.employee_pf_percent,
                pf_slab: item.pf_slab || 0,
                esi_slab: item.esi_slab || 0,
                salary_available: item.salary_available || 0,
                automate_type: item.automate_type || 0,
                employer_pf_amount: item.employer_pf_amount || 0,
                employee_pf_amount: item.employee_pf_amount || 0,
                employer_esi_amount: item.employer_esi_amount || 0,
                employee_esi_amount: item.employee_esi_amount || 0,
                gross_year: item.gross_year || 0,
                gross_month: item.gross_month || 0
            }));

            if (header[0].salary_available === "0") { if (formik.values.employee_salary_definition_type == '2') { setEmployeeSalaryDefinitionTypeStatus(true); setEmployeeSalaryDefinitionStatus(true); } else { setEmployeeSalaryDefinitionTypeStatus(false); setEmployeeSalaryDefinitionStatus(true); } }
            else {
                const gross = mapped.reduce((sum, item) => {
                    const perMonth = parseFloat(item.per_month) || 0;
                    return sum + perMonth;
                }, 0);

                formik.setFieldValue("employee_salary_definition_type", header[0]?.automate_type);
                setEmployeeSalaryDefinitionStatus(false);
                formik.setFieldValue("employer_pf_percent", header[0]?.employer_pf_percent);

                formik.setFieldValue("employee_pf_percent", header[0]?.employer_pf_percent);
                formik.setFieldValue("employee_esi_percent", header[0]?.employee_esi_percent);
                formik.setFieldValue("employer_esi_percent", header[0]?.employer_esi_percent);
                formik.setFieldValue("employer_pf_amount", header[0]?.employer_pf_amount);
                formik.setFieldValue("employee_pf_amount", header[0]?.employee_pf_amount);
                formik.setFieldValue("employer_esi_amount", header[0]?.employer_esi_amount);
                formik.setFieldValue("employee_esi_amount", header[0]?.employee_esi_amount);
                formik.setFieldValue("total_gross", gross);
                formik.setFieldValue("total_ctc", Number(gross) + (Number(header[0]?.employee_pf_amount) + Number(header[0]?.employee_esi_amount)));
                formik.setFieldValue("take_home", Number(gross) - (Number(header[0]?.employer_pf_amount) + Number(header[0]?.employer_esi_amount))); //

                formik.setFieldValue("gross_month", header[0]?.gross_month);
                formik.setFieldValue("gross_year", header[0]?.gross_year);
            }
            formik.setFieldValue("allowances", mapped);
            formik.setFieldValue("header", header[0]);
        }
    }, [employeeSalaryDefinition])

    useEffect(() => {
        if (formik.values.employee_salary_definition_type == '1') { setEmployeeSalaryDefinitionTypeStatus(true) }
        else { setEmployeeSalaryDefinitionTypeStatus(false); }
    }, [])

    const calculateGrossYear = (values) => {
        let grossYear = 0;
        if (values.employee_salary_definition_type == '2') {
            const gMonth = parseFloat(values.gross_month) || 0;
            if (Number(gMonth) > 0) { grossYear = gMonth * 12; } else { grossYear = 0; }
            setEmployeeSalaryDefinitionTypeStatus(false);
        } else {
            // dispatch(fetchEmployeeSalaryDefinitionClear());            
            const total = employeeSalaryDefinition?.emp_salarydefshow?.map(item => {
                const calculatedValue = (values.gross_month * parseFloat(item.percentage || 0)) / 100;
                const pfcalculate = parseFloat(item.is_pf === "1" ? calculatedValue.toFixed(2) : 0).toFixed(2);
                const esicalculate = parseFloat(item.is_esi === "1" ? calculatedValue.toFixed(2) : 0).toFixed(2);

                return {
                    payallwonceid: item.payallwonceid,
                    allowance: item.allowance,
                    percentage: item.percentage,
                    per_month: calculatedValue.toFixed(2),
                    per_year: (calculatedValue * 12).toFixed(2),
                    is_pf: item.is_pf,
                    is_esi: item.is_esi,
                    pfcalculate: pfcalculate,
                    esicalculate: esicalculate
                };
            });

            const gross = total?.reduce((sum, item) => {
                const perMonth = parseFloat(item.per_month) || 0;
                return sum + perMonth;
            }, 0);

            const pfmonth = total?.reduce((sum, item) => {
                const perMonth = parseFloat(item.pfcalculate) || 0;
                return sum + perMonth;
            }, 0);

            const esimonth = total?.reduce((sum, item) => {
                const perMonth = parseFloat(item.esicalculate) || 0;
                return sum + perMonth;
            }, 0);
            const pf_slab = parseFloat(formik.values.header?.pf_slab).toFixed(0);
            const pf = pfmonth?.toFixed(2) > Number(pf_slab) ? (Number(pf_slab) * parseFloat(formik.values.header?.employee_pf_percent)) / 100 : (pfmonth?.toFixed(2) * parseFloat(formik.values.header?.employee_pf_percent)) / 100 || 0;

            const esi_slab = parseFloat(formik.values.header?.esi_slab).toFixed(0);

            const esi = Number(esimonth) > Number(esi_slab) ? 0 : Math.ceil((Number(esimonth) * parseFloat(formik.values.header?.employer_esi_percent)) / 100 || 0);
            const esi_emp = Number(esimonth) > Number(esi_slab) ? 0 : Math.ceil((Number(esimonth) * parseFloat(formik.values.header?.employee_esi_percent)) / 100 || 0);

            const total_ctc = (gross + pf + esi).toFixed(2);
            const takeHomeTotal = (gross - (esi_emp + pf)).toFixed(2);
            formik.setFieldValue("employer_pf_percent", formik.values.header?.employer_pf_percent);
            formik.setFieldValue("employee_pf_percent", formik.values.header?.employer_pf_percent);
            formik.setFieldValue("employee_esi_percent", formik.values.header?.employee_esi_percent);
            formik.setFieldValue("employer_esi_percent", formik.values.header?.employer_esi_percent);

            formik.setFieldValue("employer_pf_amount", pf.toFixed(0));
            formik.setFieldValue("employee_pf_amount", pf.toFixed(0));
            formik.setFieldValue("employer_esi_amount", esi.toFixed(0));
            formik.setFieldValue("employee_esi_amount", esi_emp.toFixed(0));
            formik.setFieldValue("employee_esi_amount", esi_emp.toFixed(0));
            formik.setFieldValue("total_ctc", total_ctc);
            formik.setFieldValue("take_home", takeHomeTotal);

            formik.setFieldValue("total_gross", gross);
            formik.setFieldValue("allowances", total);
            if (Number(gross) > 0) {
                formik.setFieldValue("gross_year", gross * 12);
            } else {
                formik.setFieldValue("gross_year", 0);
            }

        }
    }

    const calculateGrossTotal = () => {
        let calculatedValue = 0, pfcalculate = 0, esicalculate = 0;

        formik.values.allowances?.forEach((item) => {
            calculatedValue += Number(item.per_month) || 0;
            pfcalculate += Number(item.is_pf === "1" ? item.per_month : 0);
            esicalculate += Number(item.is_esi === "1" ? item.per_month : 0);
        });

        const pf_slab = parseFloat(formik.values.header?.pf_slab).toFixed(0);
        const pf = Number(pfcalculate) > Number(pf_slab) ? (Number(pf_slab) * parseFloat(formik.values.header?.employee_pf_percent)) / 100 : (pfcalculate * parseFloat(formik.values.header?.employee_pf_percent)) / 100 || 0;
        const esi_slab = parseFloat(formik.values.header?.esi_slab).toFixed(0);
        const esi = Number(esicalculate) > Number(esi_slab) ? 0 : (Number(esicalculate) * parseFloat(formik.values.header?.employer_esi_percent)) / 100 || 0;
        const esi_emp = Number(esicalculate) > Number(esi_slab) ? 0 : (Number(esicalculate) * parseFloat(formik.values.header?.employee_esi_percent)) / 100 || 0;

        const total_ctc = (calculatedValue + pf + esi).toFixed(2);

        const takeHomeTotal = (calculatedValue - (esi_emp + pf)).toFixed(2);


        formik.setFieldValue("employer_pf_percent", formik.values.header?.employer_pf_percent);
        formik.setFieldValue("employee_pf_percent", formik.values.header?.employer_pf_percent);
        formik.setFieldValue("employee_esi_percent", formik.values.header?.employee_esi_percent);
        formik.setFieldValue("employer_esi_percent", formik.values.header?.employer_esi_percent);
        formik.setFieldValue("employer_pf_amount", pf.toFixed(0));
        formik.setFieldValue("employee_pf_amount", pf.toFixed(0));
        formik.setFieldValue("employer_esi_amount", esi.toFixed(0));
        formik.setFieldValue("employee_esi_amount", esi_emp.toFixed(0));
        formik.setFieldValue("employee_esi_amount", esi_emp.toFixed(0));
        formik.setFieldValue("total_ctc", total_ctc);
        formik.setFieldValue("take_home", takeHomeTotal);
        formik.setFieldValue("total_gross", calculatedValue.toFixed(0));
        formik.setFieldValue("gross_year", calculatedValue * 12);      //gross_month
        formik.setFieldValue("gross_month", calculatedValue);
    };
    const CustomSelect = ({
        closeMenuOnScroll = true,
        menuPortalTarget = document.body,
        menuPosition = "fixed",
        menuShouldScrollIntoView = false,
        ...props
    }) => {
        return (
            <Select
                {...props}
                closeMenuOnScroll={closeMenuOnScroll}
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                menuShouldScrollIntoView={menuShouldScrollIntoView}
                styles={{
                    menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                    }),
                }}
            />
        );
    };

    return (
        <React.Fragment>
            <Loader loading={loading} />
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Payroll / Salary" breadcrumbItem="Salary Definition" />
                    <Row>
                        <Col lg="12">
                            <Form onSubmit={formik.handleSubmit}>
                                <Card>
                                    <CardBody>

                                        <Row>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label>Employee <span className="text-danger">*</span></Label>
                                                    <CustomSelect
                                                        id="employee"
                                                        name="employee"
                                                        isMulti={false}
                                                        options={employees}
                                                        value={employeeDataSelected}
                                                        onChange={(selectedOption) => {
                                                            resetFormData();
                                                            formik.setFieldValue("employee", selectedOption);
                                                            setEmployeeDataSelected(selectedOption);
                                                            //formik.setFieldTouched("employee", true);
                                                            //formik.validateField('employee');
                                                            findEmpSalaryDefinition(selectedOption);
                                                            setEmployeeSalaryDefinitionTypeStatus(true)
                                                        }
                                                        }
                                                        onBlur={() => formik.setFieldTouched("employee", true)}
                                                        closeMenuOnScroll={false}
                                                        menuPosition="absolute"
                                                        menuPortalTarget={null}
                                                    />
                                                    {formik.touched.employee && formik.errors.employee && (
                                                        <div className="text-danger">{formik.errors.employee}</div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                            <Col md={4} className="text-center">
                                                <FormGroup>
                                                    <Label className="mb-2">&nbsp;</Label>
                                                    <div className="d-flex align-items-center gap-3">
                                                        <Label className="d-flex align-items-center gap-2">
                                                            <Input
                                                                type="radio"
                                                                name="employee_salary_definition_type"
                                                                value="1"
                                                                disabled={!employeeSalaryDefinitionStatus}
                                                                checked={employeeSalaryDefinitionType === '1'}
                                                                onChange={(e) => {
                                                                    formik.handleChange;
                                                                    const val = e.target.value;
                                                                    formik.setFieldValue("employee_salary_definition_type", val);
                                                                    setEmployeeSalaryDefinitionType('1');
                                                                    setEmployeeSalaryDefinitionTypeStatus(true);
                                                                    //  clearEmployeeSalaryDefinitionPercentageData();
                                                                }
                                                                }
                                                                onBlur={formik.handleBlur}
                                                            />
                                                            Percentage
                                                        </Label>
                                                        <Label className="d-flex align-items-center gap-2">
                                                            <Input
                                                                type="radio"
                                                                name="employee_salary_definition_type"
                                                                value="2"
                                                                disabled={!employeeSalaryDefinitionStatus}
                                                                checked={employeeSalaryDefinitionType === '2'}
                                                                onChange={(e) => {
                                                                    formik.handleChange;
                                                                    const val = e.target.value;
                                                                    formik.setFieldValue("employee_salary_definition_type", val);
                                                                    formik.setFieldValue("gross_month", "");
                                                                    formik.setFieldValue("total_gross", "");
                                                                    formik.setFieldValue("total_ctc", 0);
                                                                    formik.setFieldValue("take_home", 0);
                                                                    calculateGrossYear({ ...formik.values, gross_month: 0 });
                                                                    setEmployeeSalaryDefinitionType('2');
                                                                    setEmployeeSalaryDefinitionTypeStatus(false);
                                                                    // clearEmployeeSalaryDefinitionPercentageData();
                                                                }
                                                                }
                                                                // onBlur={formik.handleBlur}
                                                                onBlur={(e) => { formik.handleBlur(e); }}
                                                            />
                                                            Manual
                                                        </Label>
                                                    </div>
                                                    {formik.touched.employee_salary_definition_type && formik.errors.employee_salary_definition_type && (
                                                        <div className="text-danger">{formik.errors.employee_salary_definition_type}</div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                            {
                                                /*
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label for="category">Category *</Label>
                                                        <Input type="text" id="category" value={employeeDetails?.category || ""} disabled />
                                                    </FormGroup>
                                                </Col>
                                                */
                                            }
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label>Gross / Month <span className="text-danger">*</span></Label>
                                                    <Input
                                                        type="text"
                                                        name="gross_month"
                                                        value={formik.values.gross_month}
                                                        disabled={formik.values.employee_salary_definition_type === "2"}
                                                        onChange={(e) => {
                                                            formik.handleChange;
                                                            const val = e.target.value;
                                                            if (Number(val) < 0) { formik.setFieldValue("gross_month", ""); } else { formik.setFieldValue("gross_month", val); }
                                                            calculateGrossYear({ ...formik.values, gross_month: val });
                                                        }}
                                                        onBlur={(e) => {
                                                            formik.handleChange;
                                                            formik.setFieldTouched("gross_month", true);
                                                            //const val = e.target.value; 
                                                            //formik.setFieldValue("gross_month", val);
                                                            /*
                                                            const val = parseFloat(formik.values.gross_month);    
                                                            if (!isNaN(val)) {
                                                                //formik.setFieldValue("gross_month", val.toFixed(2));
                                                                formik.setFieldValue("gross_month", val);
                                                            } else {
                                                                formik.setFieldValue("gross_month", 0);
                                                            } 
                                                            */
                                                        }
                                                        }
                                                        onKeyDown={(e) => { if (["-", "+", "*", "/", "e", "E", "."].includes(e.key)) { e.preventDefault(); } }}
                                                        onInput={(e) => {
                                                            const value = e.target.value.replace(/[^0-9]/g, "");
                                                            formik.setFieldValue("gross_month", value);
                                                        }}
                                                        className={`text-end ${formik.errors.gross_month && formik.touched.gross_month ? "is-invalid" : ""}`}
                                                    //className={formik.errors.gross_month && formik.touched.gross_month ? "is-invalid" : ""}
                                                    />
                                                    {formik.errors.gross_month && formik.touched.gross_month && (
                                                        <div className="text-danger">{formik.errors.gross_month}</div>
                                                    )}
                                                </FormGroup>
                                            </Col>

                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label>Gross / Year <span className="text-danger">*</span></Label>
                                                    <Input
                                                        type="text"
                                                        name="gross_year"
                                                        disabled={formik.values.employee_salary_definition_type === "2"}
                                                        value={formik.values.gross_year}
                                                        onChange={formik.handleChange}
                                                        onBlur={() => {
                                                            formik.setFieldTouched("gross_year", true);
                                                            const val = parseFloat(formik.values.gross_year);
                                                            if (!isNaN(val) || Number(val) > 0) { formik.setFieldValue("gross_year", val.toFixed(2)); } else { formik.setFieldValue("gross_year", "0"); }
                                                        }
                                                        }
                                                        className={`text-end ${formik.errors.gross_year && formik.touched.gross_year ? "is-invalid" : ""}`}
                                                    />
                                                    {formik.errors.gross_year && formik.touched.gross_year && (
                                                        <div className="text-danger">{formik.errors.gross_year}</div>
                                                    )}
                                                </FormGroup>
                                            </Col>

                                            <Col md="8" className="text-end mt-4">
                                                {/* {
                                                    employeeSalaryDefinitionStatus && employeeSalaryDefinitionType == '1' &&
                                                    <Button type="button" color="primary" className="btn button-save me-2" onClick={() => { calculateGrossData(); }}>
                                                        <i className="fas fa-save me-1" />Calculate
                                                    </Button>
                                                } */}
                                                <Button type="button" className="btn button-cancel"
                                                    onClick={() => {
                                                        Swal.fire({
                                                            title: "Are you sure?", text: "Unsaved clear will be lost!", icon: "warning",
                                                            showCancelButton: true, confirmButtonText: "Yes, Clear", cancelButtonText: "No", confirmButtonColor: "#3085d6", cancelButtonColor: "#d33"
                                                        }).then((result) => {
                                                            if (result.isConfirmed) {
                                                                //  formik.resetForm 
                                                                resetFormData();
                                                            }
                                                        });
                                                    }}
                                                >
                                                    <i className="fas fa-sync me-1" /> Cancel
                                                </Button>
                                            </Col>
                                        </Row>
                                        <p className="text-danger">
                                            CAUTION: The salary working will update only for new employees alone. Any further salary revisions for existing employees have to be processed only through Increment.
                                        </p>
                                    </CardBody>
                                </Card>

                                {/* Salary Breakdown */}

                                {
                                    //(Object.keys(employeeSalaryDefinition).length>0 || employeeSalaryDefinitionType=='2') &&
                                    <Card>
                                        <CardBody>
                                            <Row>
                                                {/* Left Section */}
                                                <Col md={6}>
                                                    {formik.values.allowances?.map((item, index) => (
                                                        <FormGroup row key={item.payallwonceid}>
                                                            <Col md={5}>
                                                                <Label>{item.allowance}</Label>
                                                            </Col>
                                                            {formik.values.employee_salary_definition_type === "1" && (
                                                                <Col md={3}>
                                                                    <Input
                                                                        type="number"
                                                                        name={`allowances[${index}].percentage`}
                                                                        value={item.percentage}
                                                                        disabled
                                                                        onChange={(e) => {
                                                                            const val = e.target.value;
                                                                            formik.setFieldValue(`allowances[${index}].percentage`, val);
                                                                            calculateGrossTotal();
                                                                        }}
                                                                        onBlur={(e) => {
                                                                            const val = parseFloat(e.target.value);
                                                                            formik.setFieldValue(`allowances[${index}].percentage`, !isNaN(val) ? val.toFixed(2) : 0);
                                                                        }}
                                                                        className="text-end"
                                                                    />
                                                                </Col>
                                                            )}
                                                            <Col md={4}>
                                                                <Input
                                                                    type="number"
                                                                    name={`allowances[${index}].per_month`}
                                                                    value={item.per_month}
                                                                    disabled={formik.values.employee_salary_definition_type === "1"}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;

                                                                        formik.setFieldValue(`allowances[${index}].per_month`, val);
                                                                        formik.setFieldTouched(`allowances[${index}].per_month`, true);

                                                                        formik.validateField(`allowances[${index}].per_month`);
                                                                    }}
                                                                    onBlur={(e) => {
                                                                        const val = parseFloat(e.target.value);
                                                                        // formik.setFieldValue(`allowances[${index}].per_month`, !isNaN(val) ? val.toFixed(2) : 0);
                                                                        if (Number(val) < 0) { formik.setFieldValue(`allowances[${index}].per_month`, ""); } else { formik.setFieldValue(`allowances[${index}].per_month`, val); }
                                                                        calculateGrossTotal();
                                                                    }}
                                                                    onKeyDown={(e) => { if (["-", "+", "*", "/", "e", "E", "."].includes(e.key)) { e.preventDefault(); } }}
                                                                    onInput={(e) => {
                                                                        const value = e.target.value.replace(/[^0-9]/g, "");
                                                                        formik.setFieldValue(`allowances[${index}].per_month`, value);
                                                                    }}
                                                                    className="text-end"
                                                                />
                                                                {formik.touched.allowances?.[index]?.per_month &&
                                                                    formik.errors.allowances?.[index]?.per_month && (
                                                                        <div className="text-danger">
                                                                            {formik.errors.allowances[index].per_month}
                                                                        </div>
                                                                    )}
                                                            </Col>
                                                        </FormGroup>
                                                    ))}

                                                    {/* Gross Field */}
                                                    <FormGroup row >
                                                        <Col md={5}>
                                                            <Col md={4} className=" rounded">
                                                                <Label className="fw-bold text-uppercase">Gross</Label>
                                                            </Col>
                                                        </Col>
                                                        {formik.values.employee_salary_definition_type === "1" && (<Col md={3}></Col>)}
                                                        <Col md={4}>
                                                            <Input
                                                                type="number"
                                                                value={formik.values.total_gross}
                                                                disabled
                                                                className="text-end"
                                                            />
                                                        </Col>
                                                    </FormGroup>
                                                </Col>

                                                <Col md={6}>
                                                    <FormGroup row>
                                                        <Col md={4}>
                                                            <Label>Employer ESI</Label>
                                                        </Col>
                                                        <Col md={3}>
                                                            <Input
                                                                type="number"
                                                                name="employer_esi_percent"
                                                                disabled
                                                                value={formik.values.header?.employer_esi_percent}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    formik.setFieldValue("employer_esi_percent", val);
                                                                    calculateGrossTotal({ ...formik.values, header: val });
                                                                }
                                                                }
                                                                onBlur={formik.handleBlur}
                                                                className={`text-end ${formik.errors.employer_esi_percent && formik.touched.employer_esi_percent ? "is-invalid" : ""}`}
                                                            />
                                                            {formik.errors.employer_esi_percent && formik.touched.employer_esi_percent && (
                                                                <div className="text-danger">{formik.errors.employer_esi_percent}</div>
                                                            )}
                                                        </Col>
                                                        <Col md={3}>
                                                            <Input
                                                                type="number"
                                                                name="employer_esi_amount"
                                                                disabled
                                                                value={formik.values.employer_esi_amount}
                                                                onChange={(e) => { formik.handleChange; }}
                                                                onBlur={formik.handleBlur}
                                                                className={`text-end ${formik.errors.employer_esi_amount && formik.touched.employer_esi_amount ? "is-invalid" : ""}`}
                                                            />
                                                            {formik.errors.employer_esi_amount && formik.touched.employer_esi_amount && (
                                                                <div className="text-danger">{formik.errors.employer_esi_amount}</div>
                                                            )}
                                                        </Col>
                                                    </FormGroup>

                                                    <FormGroup row>
                                                        <Col md={4}>
                                                            <Label>Employer PF</Label>
                                                        </Col>
                                                        <Col md={3}>
                                                            <Input
                                                                type="number"
                                                                name="employer_pf_percent"
                                                                disabled
                                                                value={formik.values.header?.employer_pf_percent}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    formik.setFieldValue("employer_pf_percent", val);
                                                                    calculateGrossTotal({ ...formik.values, employer_pf_percent: val });
                                                                }
                                                                }
                                                                onBlur={formik.handleBlur}
                                                                className={`text-end ${formik.errors.employer_pf_percent && formik.touched.employer_pf_percent ? "is-invalid" : ""}`}
                                                            />
                                                            {formik.errors.employer_pf_percent && formik.touched.employer_pf_percent && (
                                                                <div className="text-danger">{formik.errors.employer_pf_percent}</div>
                                                            )}
                                                        </Col>
                                                        <Col md={3}>
                                                            <Input
                                                                type="number"
                                                                name="employer_pf_amount"
                                                                disabled
                                                                value={formik.values.employer_pf_amount}
                                                                onChange={formik.handleChange}
                                                                onBlur={formik.handleBlur}
                                                                className={`text-end ${formik.errors.employer_pf_amount && formik.touched.employer_pf_amount ? "is-invalid" : ""}`}
                                                            />
                                                            {formik.errors.employer_pf_amount && formik.touched.employer_pf_amount && (
                                                                <div className="text-danger">{formik.errors.employer_pf_amount}</div>
                                                            )}
                                                        </Col>
                                                    </FormGroup>

                                                    <FormGroup row>
                                                        <Col md={4} className=" rounded">
                                                            <Label className="fw-bold text-uppercase">Total CTC</Label>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Input
                                                                type="number"
                                                                name="total_ctc"
                                                                disabled
                                                                value={formik.values.total_ctc}
                                                                onChange={formik.handleChange}
                                                                onBlur={formik.handleBlur}
                                                                className={`text-end ${formik.errors.total_ctc && formik.touched.total_ctc ? "is-invalid" : ""}`}
                                                            />
                                                            {formik.errors.total_ctc && formik.touched.total_ctc && (
                                                                <div className="text-danger">{formik.errors.total_ctc}</div>
                                                            )}
                                                        </Col>
                                                    </FormGroup>
                                                    <FormGroup row>
                                                        <Col md={4}>
                                                            <Label>Employee ESI</Label>
                                                        </Col>
                                                        <Col md={3}>
                                                            <Input
                                                                type="number"
                                                                name="employee_esi_percent"
                                                                disabled
                                                                value={formik.values.employee_esi_percent}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    formik.setFieldValue("employee_esi_percent", val);
                                                                    calculateGrossTotal({ ...formik.values, employee_esi_percent: val });
                                                                }
                                                                }
                                                                onBlur={formik.handleBlur}
                                                                className={`text-end ${formik.errors.employee_esi_percent && formik.touched.employee_esi_percent ? "is-invalid" : ""}`}
                                                            />
                                                            {formik.errors.employee_esi_percent && formik.touched.employee_esi_percent && (
                                                                <div className="text-danger">{formik.errors.employee_esi_percent}</div>
                                                            )}
                                                        </Col>
                                                        <Col md={3}>
                                                            <Input
                                                                type="number"
                                                                name="employee_esi_amount"
                                                                disabled
                                                                value={formik.values.employee_esi_amount}
                                                                onChange={formik.handleChange}
                                                                onBlur={formik.handleBlur}
                                                                className={`text-end ${formik.errors.employee_esi_amount && formik.touched.employee_esi_amount ? "is-invalid" : ""}`}
                                                            />
                                                            {formik.errors.employee_esi_amount && formik.touched.employee_esi_amount && (
                                                                <div className="text-danger">{formik.errors.employee_esi_amount}</div>
                                                            )}
                                                        </Col>
                                                    </FormGroup>


                                                    <FormGroup row>
                                                        <Col md={4}>
                                                            <Label>Employee PF</Label>
                                                        </Col>
                                                        <Col md={3}>
                                                            <Input
                                                                type="number"
                                                                name="employee_pf_percent"
                                                                disabled
                                                                value={formik.values.header?.employee_pf_percent}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    formik.setFieldValue("employee_pf_percent", val);
                                                                    calculateGrossTotal({ ...formik.values, employee_pf_percent: val });
                                                                }
                                                                }
                                                                onBlur={formik.handleBlur}
                                                                className={`text-end ${formik.errors.employee_pf_percent && formik.touched.employee_pf_percent ? "is-invalid" : ""}`}
                                                            />
                                                            {formik.errors.employee_pf_percent && formik.touched.employee_pf_percent && (
                                                                <div className="text-danger">{formik.errors.employee_pf_percent}</div>
                                                            )}
                                                        </Col>
                                                        <Col md={3}>
                                                            <Input
                                                                type="number"
                                                                name="employee_pf_amount"
                                                                disabled
                                                                value={formik.values.employee_pf_amount}
                                                                onChange={formik.handleChange}
                                                                onBlur={formik.handleBlur}
                                                                className={`text-end ${formik.errors.employee_pf_amount && formik.touched.employee_pf_amount ? "is-invalid" : ""}`}
                                                            />
                                                            {formik.errors.employee_pf_amount && formik.touched.employee_pf_amount && (
                                                                <div className="text-danger">{formik.errors.employee_pf_amount}</div>
                                                            )}
                                                        </Col>
                                                    </FormGroup>
                                                    <FormGroup row>
                                                        <Col md={4} className="rounded">
                                                            <Label className="fw-bold text-uppercase">Take Home</Label>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Input
                                                                type="number"
                                                                name="take_home"
                                                                disabled={true}
                                                                value={formik.values.take_home}
                                                                onChange={formik.handleChange}
                                                                onBlur={formik.handleBlur}
                                                                className={`text-end ${formik.errors.take_home && formik.touched.take_home ? "is-invalid" : ""}`}
                                                            />
                                                            {formik.errors.take_home && formik.touched.take_home && (
                                                                <div className="text-danger">{formik.errors.take_home}</div>
                                                            )}
                                                        </Col>
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <Row >
                                                <Col className="text-end">
                                                    {
                                                        (employeeSalaryDefinitionStatus || employeeSalaryDefinitionCalculationStatus) &&
                                                        <>
                                                            <Button type="submit" className="btn button-save me-2"><i className="fas fa-save me-1" /> Save</Button>

                                                            <Button type="reset" className="btn button-cancel"
                                                                onClick={() => {
                                                                    Swal.fire({
                                                                        title: "Are you sure?", text: "Unsaved clear will be lost!", icon: "warning",
                                                                        showCancelButton: true, confirmButtonText: "Yes, Clear", cancelButtonText: "No", confirmButtonColor: "#3085d6", cancelButtonColor: "#d33"
                                                                    }).then((result) => {
                                                                        if (result.isConfirmed) {
                                                                            //  formik.resetForm 
                                                                            resetFormData();
                                                                        }
                                                                    });
                                                                }}
                                                            >
                                                                <i className="fas fa-sync me-1" /> Cancel
                                                            </Button>
                                                        </>
                                                    }
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                }

                            </Form>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default SalaryDefinition;
